import React from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
} from "reactstrap";

import { faqItems, planIncludes, pricingTiers } from "./marketingData";
import "./marketing.css";
import trackLastMileLogo from "../../assets/images/tracklastmile-logo.svg";

const features = [
  {
    title: "Customer App (iOS + Android)",
    icon: "mdi mdi-cellphone",
    description:
      "Your customers browse stores, place orders, pay in-app, and track their driver live on a map just like the big delivery apps they already use.",
  },
  {
    title: "Store Portal",
    icon: "mdi mdi-storefront",
    description:
      "Onboarded stores log in to manage their menu, accept incoming orders, and track fulfillment in real time. No training required.",
  },
  {
    title: "Driver App (iOS + Android)",
    icon: "mdi mdi-car",
    description:
      "Drivers receive assignments, navigate with Google Maps, and capture proof of delivery with signature and photo. Built for speed in the field.",
  },
  {
    title: "Admin Dashboard",
    icon: "mdi mdi-cog-outline",
    description:
      "You orchestrate the entire operation: onboard stores and drivers, assign orders, monitor live deliveries, generate invoices, and pull detailed reports.",
  },
];

const whyItems = [
  {
    title: "Launch fast",
    icon: "mdi mdi-rocket-launch",
    description:
      "Skip the 12-month build cycle. Our platform is production-ready today. Add your logo, your colors, your city, and start delivering.",
  },
  {
    title: "Keep 100% of your margins",
    icon: "mdi mdi-cash-multiple",
    description:
      "Stop paying DoorDash 30% per order. Run your own delivery service and own your customer relationships, your data, and your profit.",
  },
  {
    title: "Built for any market",
    icon: "mdi mdi-earth",
    description:
      "Stripe payments, Twilio SMS, Google Maps, multi-store, multi-driver, and multi-language support. Launch in Toronto, Lagos, Karachi, Dubai, and beyond.",
  },
];

const TrackLastMileHome = () => {
  return (
    <div className="tlm-home tlm-dark min-vh-100">
      <section className="tlm-hero py-5">
        <div className="tlm-glow tlm-glow-one" />
        <div className="tlm-glow tlm-glow-two" />
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg="10">
              <div className="d-flex justify-content-center mb-4">
                <img
                  src={trackLastMileLogo}
                  alt="TrackLastMile"
                  className="img-fluid tlm-home-logo"
                />
              </div>
              <p className="text-uppercase tlm-eyebrow font-weight-bold small mb-3">
                The All-in-One Delivery Platform
              </p>
              <h1 className="display-4 font-weight-bold mb-3 text-dark">
                Launch Your Own Branded Delivery Service - In Weeks, Not Years
              </h1>
              <p className="lead tlm-text-muted mb-4">
                TrackLastMile gives you everything DoorDash and Uber Eats spent
                millions building: a customer ordering app, store portal,
                driver app, and admin dashboard fully branded as your business.
                Start taking orders in your city in under 30 days.
              </p>
              <div className="d-flex flex-wrap justify-content-center mb-3">
                <Link
                  to="/login"
                  className="btn btn-primary m-2 px-4 tlm-btn-primary"
                >
                  See Live Demo
                </Link>
                <Button
                  color="outline-light"
                  tag={Link}
                  to="/pricing"
                  className="m-2 px-4 tlm-btn-outline"
                >
                  View Pricing
                </Button>
              </div>
              <div className="d-flex flex-wrap justify-content-center mt-3">
                <span className="tlm-chip">Deploy anywhere</span>
                <span className="tlm-chip">Stripe-powered payments</span>
                <span className="tlm-chip">Live GPS tracking</span>
                <span className="tlm-chip">iOS + Android ready</span>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="py-5 tlm-section">
        <Container>
          <Row className="mb-4">
            <Col lg="8">
              <h2 className="font-weight-bold mb-2 text-dark">
                Everything you need to run a delivery business. Out of the box.
              </h2>
            </Col>
          </Row>
          <Row>
            {features.map((item) => (
              <Col md="6" className="mb-4" key={item.title}>
                <Card className="h-100 tlm-card border-0">
                  <CardBody>
                    <h4 className="h5 mb-3 text-dark">
                      <i className={`${item.icon} tlm-icon mr-2`} />
                      {item.title}
                    </h4>
                    <p className="tlm-text-muted mb-0">{item.description}</p>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="py-5 tlm-section tlm-section-alt">
        <Container>
          <Row className="mb-4">
            <Col>
              <h2 className="font-weight-bold mb-0 text-dark">
                Built by operators. Priced for founders.
              </h2>
            </Col>
          </Row>
          <Row>
            {whyItems.map((item) => (
              <Col md="4" className="mb-4" key={item.title}>
                <Card className="h-100 border-0 tlm-card">
                  <CardBody>
                    <h3 className="h5 mb-3 text-dark">
                      <i className={`${item.icon} tlm-icon-success mr-2`} />
                      {item.title}
                    </h3>
                    <p className="tlm-text-muted mb-0">{item.description}</p>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="py-5 tlm-section" id="pricing">
        <Container>
          <Row className="mb-4">
            <Col>
              <h2 className="font-weight-bold mb-2 text-dark">
                Simple pricing. Real numbers. No "contact us" games.
              </h2>
            </Col>
          </Row>
          <Row>
            {pricingTiers.map((plan) => (
              <Col lg="3" md="6" className="mb-4" key={plan.key}>
                <Card
                  className={`h-100 border tlm-card ${
                    plan.featured ? "border-primary tlm-card-featured" : ""
                  }`}
                >
                  <CardBody className="d-flex flex-column">
                    <div className="mb-2">
                      <h3 className="h5 mb-1 d-inline-block text-dark">{plan.title}</h3>
                      {plan.badge ? (
                        <Badge color="primary" pill className="ml-2 tlm-badge">
                          {plan.badge}
                        </Badge>
                      ) : null}
                    </div>
                    <p className="h5 mb-2 text-dark">{plan.price}</p>
                    <p className="tlm-text-muted mb-2">{plan.meter}</p>
                    <p className="tlm-text-muted mb-2">{plan.branding}</p>
                    <p className="tlm-text-muted mb-2">{plan.limit}</p>
                    <p className="tlm-text-muted mb-4">{plan.support}</p>
                    <Button
                      color={plan.featured ? "primary" : "outline-light"}
                      tag={Link}
                      to={plan.ctaLink}
                      className={`mt-auto ${plan.featured ? "tlm-btn-primary" : "tlm-btn-outline"}`}
                    >
                      {plan.ctaLabel}
                    </Button>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
          <p className="tlm-text-muted mb-0">{planIncludes}</p>
        </Container>
      </section>

      <section className="py-5 tlm-section tlm-section-alt">
        <Container>
          <Row className="justify-content-center">
            <Col lg="10">
              <h2 className="font-weight-bold mb-3 text-dark">
                Built by a small team that gets it.
              </h2>
              <p className="tlm-text-muted mb-3">
                TrackLastMile was built by a team of three engineers who
                watched local restaurants and couriers lose 30% margins to
                delivery aggregators, or struggle to run their own deliveries
                with spreadsheets and chat apps.
              </p>
              <p className="tlm-text-muted mb-4">
                We built the tool we wished existed: a complete delivery
                platform you can deploy and brand as your own, without spending
                a million dollars or two years building it. Today,
                TrackLastMile is live and ready to power your delivery business
                wherever in the world you operate.
              </p>
              <Button color="primary" className="tlm-btn-primary" tag={Link} to="/trial-register">
                Book a 15-minute demo
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="py-5 tlm-section">
        <Container>
          <Row className="mb-4">
            <Col>
              <h2 className="font-weight-bold mb-0 text-dark">FAQ</h2>
            </Col>
          </Row>
          <Row>
            {faqItems.map((item) => (
              <Col md="6" className="mb-4" key={item.question}>
                <Card className="h-100 border-0 tlm-card">
                  <CardBody>
                    <h3 className="h6 font-weight-bold mb-2 text-dark">{item.question}</h3>
                    <p className="tlm-text-muted mb-0">{item.answer}</p>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="py-5 tlm-footer-cta text-center">
        <Container>
          <h2 className="display-5 font-weight-bold mb-4 text-dark">
            Ready to run your own delivery service?
          </h2>
          <Button color="light" tag={Link} to="/start-free-trial" className="m-2 tlm-btn-light">
            Start Free Trial
          </Button>
          <Button color="outline-light" tag={Link} to="/trial-register" className="m-2 tlm-btn-outline">
            Book a Demo
          </Button>
        </Container>
      </section>
    </div>
  );
};

export default TrackLastMileHome;
